import type { ModuleIndex } from './module-filter';
import type { ModuleId } from './module-labels';
import { getComponentSchemas, hasAnySelected, isObject, resolveRefs, type JsonObject } from './openapi-utils';

/** Placeholder returned for a property that should be left out of the example */
const OMIT = Symbol('omit');

/** Values tried in order for numbers, keeping the first one the schema's bounds allow */
const FILLED_NUMBER_CANDIDATES = [10, 0.5, 1, 100];
const EMPTY_NUMBER_CANDIDATES = [0, 1, 0.5];
const EXAMPLE_DATE = '2024-07-01';
const EXAMPLE_STRING = 'example';

/**
 * Adjusts a generated value for rules the schema can't express, e.g. a group of fractions that must add up to 1.
 * Applied to the finished example, so it sees the whole group regardless of which fields the selection included.
 */
export type ExampleOverride = {
  /** Property names leading to the value, matched against the end of its path */
  path: readonly string[];
  apply: (value: unknown) => unknown;
};

const endsWith = (path: readonly string[], suffix: readonly string[]) =>
  suffix.length <= path.length && suffix.every((name, i) => path[path.length - suffix.length + i] === name);

function applyOverrides(value: unknown, overrides: readonly ExampleOverride[], path: readonly string[]): unknown {
  let result = value;
  if (Array.isArray(value)) {
    result = value.map((item) => applyOverrides(item, overrides, path));
  } else if (isObject(value)) {
    result = Object.fromEntries(
      Object.entries(value).map(([name, child]) => [name, applyOverrides(child, overrides, [...path, name])]),
    );
  }
  for (const override of overrides) {
    if (endsWith(path, override.path)) result = override.apply(result);
  }
  return result;
}

/** Writing a schema of {"not": {}} means the field must always be left out */
const isNever = (schema: JsonObject) => isObject(schema.not) && Object.keys(schema.not).length === 0;

const baseType = (schema: JsonObject): string | undefined => {
  if (typeof schema.type === 'string') return schema.type;
  if (Array.isArray(schema.type)) return schema.type.find((t) => t !== 'null') as string | undefined;
  if (isObject(schema.properties) || isObject(schema.additionalProperties)) return 'object';
  if (isObject(schema.items)) return 'array';
  return undefined;
};

function numberValue(schema: JsonObject, filled: boolean): number {
  const isInteger = schema.type === 'integer';
  const num = (key: string) => (typeof schema[key] === 'number' ? (schema[key] as number) : undefined);
  const [min, max, exclusiveMin, exclusiveMax] = [
    num('minimum'),
    num('maximum'),
    num('exclusiveMinimum'),
    num('exclusiveMaximum'),
  ];
  const valid = (value: unknown): value is number =>
    typeof value === 'number' &&
    (min === undefined || value >= min) &&
    (max === undefined || value <= max) &&
    (exclusiveMin === undefined || value > exclusiveMin) &&
    (exclusiveMax === undefined || value < exclusiveMax) &&
    (!isInteger || Number.isInteger(value));

  // Prefer values the schema author chose, as long as they suit what is being asked for
  const preferred = [schema.example, schema.default].filter(valid);
  const candidates = filled ? [...preferred, ...FILLED_NUMBER_CANDIDATES] : [...preferred, ...EMPTY_NUMBER_CANDIDATES];
  const found = candidates.find((v) => valid(v) && (!filled || v !== 0));
  if (found !== undefined) return found;

  // Fall back to the middle of the allowed range
  const lo = min ?? exclusiveMin ?? 0;
  const hi = max ?? exclusiveMax ?? lo + 20;
  const middle = (lo + hi) / 2;
  return isInteger ? Math.round(middle) : middle;
}

function stringValue(schema: JsonObject, filled: boolean): string {
  if (schema.format === 'date') return EXAMPLE_DATE;
  if (schema.format === 'date-time') return `${EXAMPLE_DATE}T00:00:00Z`;
  if (typeof schema.default === 'string') return schema.default;
  return filled ? EXAMPLE_STRING : '';
}

/**
 * Builds an example request body shaped by the selected modules:
 * - fields tagged with a selected module, and everything they require, are filled in with non empty values,
 *   i.e. numbers other than 0 and arrays with at least one item
 * - optional fields unrelated to the selection are left out
 * - required fields unrelated to the selection are kept but left empty, i.e. 0, "" and []
 * - for oneOf/anyOf, the first variant that leads to a selected module is used, falling back to the first
 *
 * With nothing selected the result is the smallest valid skeleton of the request.
 * Overrides are applied last, to satisfy validation the schema doesn't describe.
 */
export function generateModuleExample(
  spec: JsonObject,
  index: ModuleIndex,
  bodySchema: JsonObject,
  selected: ReadonlySet<ModuleId>,
  overrides: readonly ExampleOverride[] = [],
): unknown {
  const components = getComponentSchemas(spec);

  // Lookups are keyed on the schema objects as they appear in the document, before any $ref is followed
  const leadsToSelection = (raw: unknown) => isObject(raw) && hasAnySelected(index.subtreeModules.get(raw), selected);
  const ownMatch = (schema: JsonObject) =>
    Array.isArray(schema['x-modules']) && hasAnySelected(schema['x-modules'], selected);

  const pickVariant = (variants: unknown[]) => variants.find(leadsToSelection) ?? variants[0];

  /**
   * Picks array items so that every selected module the items can lead to is shown at least once, e.g. a
   * stationary fuel list gets one solid and one gaseous fuel when their modules differ.
   */
  const pickArrayVariants = (variants: unknown[]) => {
    const covered = new Set<ModuleId>();
    const picked: unknown[] = [];
    for (const variant of variants) {
      if (!isObject(variant)) continue;
      const modules = [...(index.subtreeModules.get(variant) ?? [])].filter((m) => selected.has(m));
      if (modules.some((m) => !covered.has(m))) {
        picked.push(variant);
        modules.forEach((m) => covered.add(m));
      }
    }
    return picked;
  };

  /** Returns the example value for a schema, or OMIT when the field should be left out */
  const generate = (raw: JsonObject, filled: boolean, stack: readonly string[]): unknown => {
    if (isNever(raw)) return OMIT;
    // A circular $ref would never end, so the field is left out
    const resolved = resolveRefs(raw, components, stack);
    if (resolved.circular) return OMIT;
    const { schema } = resolved;
    // Everything beneath a field used by a selected module is part of that field, so is filled in too.
    // The resolved schema keeps x-modules declared next to a $ref, so checking it covers the raw schema too.
    const fill = filled || ownMatch(schema);

    if ('const' in schema) return schema.const;
    if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

    if (Array.isArray(schema.allOf)) {
      const { allOf, ...outer } = schema;
      const parts = [...allOf, outer].filter(isObject).map((member) => generate(member, fill, resolved.stack));
      const objects = parts.filter(isObject);
      if (objects.length > 0) return Object.assign({}, ...objects);
      return parts.find((part) => part !== OMIT) ?? OMIT;
    }

    for (const keyword of ['oneOf', 'anyOf'] as const) {
      const variants = schema[keyword];
      if (!Array.isArray(variants) || variants.length === 0) continue;
      const variant = pickVariant(variants);
      if (!isObject(variant)) return OMIT;
      const { [keyword]: _variants, ...outer } = schema;
      const variantValue = generate(variant, fill, resolved.stack);
      // Fields declared next to the composition apply to whichever variant is used
      const outerValue = isObject(outer.properties) ? generate(outer, fill, resolved.stack) : OMIT;
      return isObject(variantValue) && isObject(outerValue) ? { ...outerValue, ...variantValue } : variantValue;
    }

    switch (baseType(schema)) {
      case 'object':
        return generateObject(schema, fill, resolved.stack);
      case 'array':
        return generateArray(schema, fill, resolved.stack);
      case 'number':
      case 'integer':
        return numberValue(schema, fill);
      case 'boolean':
        return fill ? true : typeof schema.default === 'boolean' ? schema.default : false;
      case 'string':
        return stringValue(schema, fill);
      case 'null':
        return null;
      default:
        return fill ? EXAMPLE_STRING : null;
    }
  };

  const generateObject = (schema: JsonObject, filled: boolean, stack: readonly string[]) => {
    const result: JsonObject = {};
    const required = new Set(Array.isArray(schema.required) ? schema.required.map(String) : []);

    if (isObject(schema.properties)) {
      for (const [name, child] of Object.entries(schema.properties)) {
        if (!isObject(child)) continue;
        if (!required.has(name) && !leadsToSelection(child)) continue;
        const value = generate(child, filled, stack);
        if (value !== OMIT) result[name] = value;
      }
    }

    if (isObject(schema.additionalProperties) && leadsToSelection(schema.additionalProperties)) {
      const value = generate(schema.additionalProperties, filled, stack);
      // Maps get a single placeholder entry
      if (value !== OMIT) result.key = value;
    }

    return result;
  };

  const generateArray = (schema: JsonObject, filled: boolean, stack: readonly string[]) => {
    const items = isObject(schema.items) ? schema.items : undefined;
    if (!items) return [];
    const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;

    let values: unknown[] = [];
    const resolvedItems = resolveRefs(items, components, stack);
    const variants = resolvedItems.circular ? undefined : (resolvedItems.schema.oneOf ?? resolvedItems.schema.anyOf);
    if (!filled && Array.isArray(variants) && leadsToSelection(items)) {
      // One item per variant needed to cover the selected modules
      values = pickArrayVariants(variants).map((variant) =>
        generate(variant as JsonObject, false, resolvedItems.stack),
      );
    } else if (filled || leadsToSelection(items)) {
      // A single item, filled in when the array is part of a selected field
      values = [generate(items, filled, stack)];
    }

    // Pad out to the minimum length with empty items
    while (values.length < minItems) values.push(generate(items, false, stack));
    return values.filter((value) => value !== OMIT);
  };

  const value = generate(bodySchema, false, []);
  return value === OMIT ? undefined : applyOverrides(value, overrides, []);
}
